import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MTX_DRAWER_DATA, MtxDrawerRef } from '@ng-matero/extensions/drawer';
import { SiteTourService } from '@app/shared/services/site-tours.service';
import { HelpersService } from '@app/shared/services/helpers.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { jsonValidator } from '@app/shared/validators/json.validator';
import { IconOption } from '@app/shared/services/icon.service';
import { v4 as uuidv4 } from 'uuid';

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left';
type HotspotPosition = 'left' | 'right' | 'top' | 'bottom';

interface HotspotConfig {
  id: string;
  target: string;
  className?: string;
  position?: HotspotPosition;
  icon?: {
    type: 'pulsing-dot' | 'site-icon' | 'image' | 'lottie';
    src?: string;
    color?: string;
    loop?: boolean;
    autoplay?: boolean;
    renderer?: 'svg' | 'canvas' | 'html';
    rendererSettings?: {
      preserveAspectRatio?: string;
      progressiveLoad?: boolean;
      hideOnTransparent?: boolean;
    };
    className?: string;
    height?: number;
    width?: number;
    alt?: string;
  };
  offset?: {
    x?: number;
    y?: number;
  };
  tooltip?: {
    content: string;
    position?: TooltipPosition;
    className?: string;
    backgroundColor?: string;
    textColor?: string;
    maxWidth?: number;
    icon?: {
      type: 'site-icon' | 'lottie' | 'image';
      src?: string;
      color?: string;
      loop?: boolean;
      autoplay?: boolean;
      renderer?: 'svg' | 'canvas' | 'html';
      rendererSettings?: {
        preserveAspectRatio?: string;
        progressiveLoad?: boolean;
        hideOnTransparent?: boolean;
      };
      className?: string;
      height?: number;
      width?: number;
      alt?: string;
    };
  };
  zIndex?: number;
}

@Component({
  standalone: false,
  selector: 'app-edit-site-hotspots',
  templateUrl: './edit-hotspots.component.html',
})
export class EditHotspotsComponent implements OnInit {
  public isEditing: boolean;
  public loading: boolean = true;
  public hotspot: any;
  public isSubmitting = false;
  public form: FormGroup;

  readonly tooltipPositions: TooltipPosition[] = ['top', 'right', 'bottom', 'left'];
  readonly hotspotPositions: HotspotPosition[] = ['left', 'right', 'top', 'bottom'];
  readonly hotspotIconTypes = ['pulsing-dot', 'site-icon', 'image']; // 'lottie' is untested currently
  readonly tooltipIconTypes = ['site-icon', 'image']; // 'lottie' is untested currently

  constructor(
    public drawerRef: MtxDrawerRef<EditHotspotsComponent>,
    @Inject(MTX_DRAWER_DATA) public data: { hotspot?: any },
    private formBuilder: FormBuilder,
    private siteTourService: SiteTourService,
    private helpersService: HelpersService,
    private logger: LoggerService,
    private bannerNotificationsService: BannerNotificationsService
  ) {}

  ngOnInit() {
    this.loading = true;
    this.hotspot = this.data.hotspot;
    this.isEditing = !!this.hotspot;
    this.createForm();
  }

  public dismissDrawer(result: boolean = false): void {
    this.drawerRef.dismiss(result);
  }

  public createForm(): void {
    const defaultConfig: HotspotConfig = {
      id: '',
      target: '',
      position: 'right',
      zIndex: 1031,
      icon: {
        type: 'pulsing-dot',
      },
      tooltip: {
        content: '',
        position: 'right',
        backgroundColor: '#225976',
        textColor: '#FFFFFF',
        maxWidth: 300,
      },
    };

    const initialConfig = this.isEditing ? this.hotspot.settings : defaultConfig;

    this.form = this.formBuilder.group({
      name: [this.hotspot?.name || '', [Validators.required]],
      pageSlug: [this.hotspot?.pageSlug || '', [Validators.required]],
      isActive: [this.hotspot?.isActive ?? 1],
      settings: this.formBuilder.group({
        id: [initialConfig.id],
        target: [initialConfig.target, [Validators.required]],
        position: [initialConfig.position],
        zIndex: [initialConfig.zIndex],
        icon: this.formBuilder.group({
          type: [initialConfig.icon?.type],
          src: [initialConfig.icon?.src],
          color: [initialConfig.icon?.color || '#ffcc00'],
          className: [initialConfig.icon?.className],
          height: [initialConfig.icon?.height],
          width: [initialConfig.icon?.width],
          alt: [initialConfig.icon?.alt],
        }),
        offset: this.formBuilder.group({
          x: [initialConfig.offset?.x],
          y: [initialConfig.offset?.y],
        }),
        tooltip: this.formBuilder.group({
          content: [initialConfig.tooltip?.content, [Validators.required]],
          position: [initialConfig.tooltip?.position],
          backgroundColor: [initialConfig.tooltip?.backgroundColor],
          textColor: [initialConfig.tooltip?.textColor],
          maxWidth: [initialConfig.tooltip?.maxWidth],
          icon: this.formBuilder.group({
            type: [initialConfig.tooltip?.icon?.type || ''],
            src: [initialConfig.tooltip?.icon?.src],
            color: [
              initialConfig.tooltip?.icon?.type === 'site-icon'
                ? initialConfig.tooltip?.icon?.color || '#ffcc00'
                : initialConfig.tooltip?.icon?.color,
            ],
            loop: [initialConfig.tooltip?.icon?.loop],
            autoplay: [initialConfig.tooltip?.icon?.autoplay],
            renderer: [initialConfig.tooltip?.icon?.renderer],
            height: [initialConfig.tooltip?.icon?.height],
            width: [initialConfig.tooltip?.icon?.width],
            alt: [initialConfig.tooltip?.icon?.alt],
            rendererSettings: this.formBuilder.group({
              preserveAspectRatio: [initialConfig.tooltip?.icon?.rendererSettings?.preserveAspectRatio],
              progressiveLoad: [initialConfig.tooltip?.icon?.rendererSettings?.progressiveLoad],
              hideOnTransparent: [initialConfig.tooltip?.icon?.rendererSettings?.hideOnTransparent],
            }),
            className: [initialConfig.tooltip?.icon?.className],
          }),
        }),
      }),
    });

    this.loading = false;
  }

  public onHotspotIconSelected(event: IconOption): void {
    const settingsForm = this.form.get('settings') as FormGroup;
    const iconForm = settingsForm.get('icon') as FormGroup;
    iconForm.patchValue({
      type: 'site-icon',
      src: event.className,
      color: '#ffcc00',
    });
  }

  public onTooltipIconSelected(event: IconOption): void {
    const settingsForm = this.form.get('settings') as FormGroup;
    const tooltipForm = settingsForm.get('tooltip') as FormGroup;
    const tooltipIconForm = tooltipForm.get('icon') as FormGroup;
    tooltipIconForm.patchValue({
      type: 'site-icon',
      src: event.className,
      color: '#ffcc00',
    });
  }

  public onSubmit(): void {
    if (this.form.invalid) {
      return;
    }

    this.isSubmitting = true;
    const formValue = this.form.value;
    const hotspotId = formValue.name.toLowerCase().replace(/ /g, '-');
    formValue.settings.id = `hotspot-${hotspotId}`;

    const hotspotData = {
      name: formValue.name,
      pageSlug: formValue.pageSlug,
      elementSelector: formValue.settings.target,
      position: formValue.settings.position,
      tooltipContent: formValue.settings.tooltip.content,
      isActive: formValue.isActive === 1,
      settings: formValue.settings,
      zIndex: formValue.settings.zIndex,
    };

    if (this.isEditing) {
      this.siteTourService.updateHotspot(this.hotspot.id, hotspotData).subscribe({
        next: () => {
          this.bannerNotificationsService.success('Hotspot updated successfully');
          this.dismissDrawer(true);
        },
        error: (error) => {
          this.logger.error('Failed to update hotspot', error);
          this.bannerNotificationsService.error('Failed to update hotspot');
          this.isSubmitting = false;
        },
      });
    } else {
      this.siteTourService.createHotspot(hotspotData).subscribe({
        next: () => {
          this.bannerNotificationsService.success('Hotspot created successfully');
          this.dismissDrawer(true);
        },
        error: (error) => {
          this.logger.error('Failed to create hotspot', error);
          this.bannerNotificationsService.error('Failed to create hotspot');
          this.isSubmitting = false;
        },
      });
    }
  }
}
